import { useAuthStore } from '@stores/authStore';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

/**
 * Cliente WebSocket de la app, apoyado en Laravel Reverb (protocolo Pusher).
 *
 * Reverb habla el protocolo de Pusher, por eso usamos `pusher-js` como transporte
 * y `laravel-echo` como capa de suscripción a canales. El backend emite eventos
 * de broadcast (p. ej. `notification.emitted`) en el canal privado del usuario
 * `App.Models.User.{id}` — ver backend `Src\Notification\Infrastructure\Broadcasting\NotificationEmitted`.
 *
 * La autorización de canales privados pega contra `/broadcasting/auth` del backend,
 * que está protegido por `auth.jwt`: por eso el authorizer manda el Bearer token
 * (JWT) leído en fresco del authStore en cada intento de suscripción.
 */

declare global {
  // pusher-js necesita `window.Pusher` disponible cuando el broadcaster es "reverb".

  var Pusher: typeof import('pusher-js').default;

  var Echo: import('laravel-echo').default<'reverb'> | undefined;
}

let echoInstance: Echo<'reverb'> | null = null;

/**
 * Corta la reautorización en bucle contra `/broadcasting/auth`.
 *
 * pusher-js vuelve a llamar al authorizer en CADA reconexión del WebSocket
 * (`subscribeAll()` al evento `connected` -> `Channel.subscribe()` -> `authorize()`),
 * y si el socket se cae reintenta conectar cada segundo de forma indefinida.
 * Cuando la autorización nunca pasa (401/403, 404, CORS), ese ciclo quema una
 * request completa del stack de Laravel por cada intento y no consigue nada.
 *
 * Tras MAX_AUTH_FAILURES fallos consecutivos se deja de pedir autorización; un
 * único éxito reinicia el contador, así que un fallo puntual de red no apaga el
 * tiempo real. Sin WebSocket el contador sigue cargándose al montar y al
 * recuperar el foco (ver useUnreadCount).
 */
const MAX_AUTH_FAILURES = 5;
let consecutiveAuthFailures = 0;

/**
 * URL de autorización de canales privados. Se toma de VITE_REVERB_AUTH_ENDPOINT
 * si está definida; si no, se deriva del origin de VITE_API_WH
 * (`https://host/api/v1/` -> `https://host/broadcasting/auth`).
 */
function resolveAuthEndpoint(): string {
  const explicit = import.meta.env.VITE_REVERB_AUTH_ENDPOINT;
  if (explicit) return explicit;

  const apiBase = import.meta.env.VITE_API_WH;
  try {
    return new URL('/broadcasting/auth', apiBase).toString();
  } catch {
    // Fallback defensivo si VITE_API_WH no es una URL absoluta válida.
    return '/broadcasting/auth';
  }
}

/**
 * Indica si hay configuración suficiente para habilitar WebSockets.
 * Sin app key el cliente no puede conectar; en ese caso los consumidores
 * deben degradar a polling sin romper.
 */
export function isEchoEnabled(): boolean {
  return Boolean(import.meta.env.VITE_REVERB_APP_KEY);
}

/**
 * Devuelve el singleton de Echo, creándolo la primera vez. Retorna null si
 * no hay configuración de Reverb (el front sigue funcionando con polling).
 */
export function getEcho(): Echo<'reverb'> | null {
  if (!isEchoEnabled()) return null;
  if (echoInstance) return echoInstance;

  window.Pusher = Pusher;

  const scheme = import.meta.env.VITE_REVERB_SCHEME ?? 'https';
  const forceTLS = scheme === 'https';
  const port = Number(import.meta.env.VITE_REVERB_PORT ?? (forceTLS ? 443 : 8080));
  const authEndpoint = resolveAuthEndpoint();

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: import.meta.env.VITE_REVERB_APP_KEY,
    wsHost: import.meta.env.VITE_REVERB_HOST ?? window.location.hostname,
    wsPort: port,
    wssPort: port,
    forceTLS,
    enabledTransports: ['ws', 'wss'],
    // Autorización manual de canales privados: manda el JWT actual en cada intento.
    authorizer: (channel: { name: string }) => ({
      authorize: (socketId, callback) => {
        if (consecutiveAuthFailures >= MAX_AUTH_FAILURES) {
          callback(new Error(`Broadcasting auth desactivado tras ${MAX_AUTH_FAILURES} fallos seguidos`), null);
          return;
        }
        const token = useAuthStore.getState().token;
        fetch(authEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ socket_id: socketId, channel_name: channel.name }),
        })
          .then(async (response) => {
            // Leemos el body como texto SIEMPRE (aunque falle) para poder depurar:
            // el 403/401 de Laravel suele traer un JSON/HTML con el motivo real que
            // pusher-js descarta (por eso en consola solo se ve `error: undefined`).
            const rawBody = await response.text();
            if (!response.ok) {
              consecutiveAuthFailures += 1;
              console.error(`[Echo] /broadcasting/auth FALLÓ para canal "${channel.name}"`, {
                status: response.status,
                statusText: response.statusText,
                authEndpoint,
                hasToken: Boolean(token),
                body: rawBody,
              });
              callback(new Error(`Broadcasting auth ${response.status}: ${rawBody}`), null);
              return;
            }
            // Éxito: el body debe ser el JSON con la firma de auth.
            const data = rawBody ? JSON.parse(rawBody) : {};
            consecutiveAuthFailures = 0;
            callback(null, data);
          })
          .catch((error) => {
            consecutiveAuthFailures += 1;
            // Fallo de red / CORS / JSON inválido: aquí no hay respuesta HTTP.
            console.error(`[Echo] /broadcasting/auth error de red para canal "${channel.name}"`, {
              authEndpoint,
              hasToken: Boolean(token),
              error,
            });
            callback(error instanceof Error ? error : new Error('Broadcasting auth error'), null);
          });
      },
    }),
  });

  // Logs de estado de la conexión WS (útil para depurar conexión con Reverb).
  const connection = echoInstance.connector.pusher.connection;
  connection.bind('state_change', (states: { previous: string; current: string }) => {
    console.log(`[Echo] estado WS: ${states.previous} -> ${states.current}`);
  });
  connection.bind('connected', () => {
    console.log('[Echo] WebSocket conectado a Reverb', { socketId: echoInstance?.socketId() });
  });
  connection.bind('error', (error: unknown) => {
    console.error('[Echo] error de conexión WS', error);
  });
  console.log('[Echo] instancia creada, intentando conectar...', {
    wsHost: import.meta.env.VITE_REVERB_HOST ?? window.location.hostname,
    wsPort: port,
    forceTLS,
  });

  window.Echo = echoInstance;
  return echoInstance;
}

/**
 * Cierra la conexión WS y limpia el singleton. Llamar al hacer logout para
 * no dejar el socket colgado autenticado con un token que ya no es válido.
 */
export function disconnectEcho(): void {
  consecutiveAuthFailures = 0;
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
    window.Echo = undefined;
  }
}
